/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\PickupGroupMembersBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Applications module.
----------------------------------------------------------------*/


import { Users, Trash2, Plus } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Button } from '../../../components/ui/button';
import Toggle from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import type { PickupGroupMember } from '../types';

interface AvailableExtension {
    id: string;
    name: string;
}

interface MembersTableRow {
    member: PickupGroupMember;
    index: number;
    availableForSelect: AvailableExtension[];
}

interface PickupGroupMembersBoxProps {
    membersTable: MembersTableRow[];
    errors: Record<string, string>;
    onMemberChange: (index: number, field: keyof PickupGroupMember, value: string | boolean) => void;
    onMemberRemove: (index: number) => void;
    onAddMember: () => void;
    t: (key: string, fallback?: string) => string;
}

export function PickupGroupMembersBox({
    membersTable, errors, onMemberChange, onMemberRemove, onAddMember, t
}: PickupGroupMembersBoxProps) {
    return (
        <FormBox title={t('pickupGroups.fields.members.label')} icon={<Users className="h-5 w-5" />}>
            <div className="space-y-4">
                {errors.members && <p className="text-sm text-red-500">{errors.members}</p>}

                <table className="w-full border-collapse table-fixed">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '40%' }}>
                                <SimpleTooltip content={t('pickupGroups.fields.members.extensionTooltip')}>
                                    <span>{t('pickupGroups.fields.members.extension')}</span>
                                </SimpleTooltip>
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '25%' }}>
                                <SimpleTooltip content={t('pickupGroups.fields.members.isMemberTooltip')}>
                                    <span>{t('pickupGroups.fields.members.isMember')}</span>
                                </SimpleTooltip>
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-foreground" style={{ width: '25%' }}>
                                <SimpleTooltip content={t('pickupGroups.fields.members.allowPickupTooltip')}>
                                    <span>{t('pickupGroups.fields.members.allowPickup')}</span>
                                </SimpleTooltip>
                            </th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-foreground" style={{ width: '10%' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {membersTable.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="text-center py-8 text-muted-foreground">
                                    {t('pickupGroups.members.empty')}
                                </td>
                            </tr>
                        ) : (
                            membersTable.map(({ member, index, availableForSelect }) => (
                                <tr key={`${member.extension}-${index}`} className="border-b border-border hover:bg-muted/30">
                                    <td className="py-3 px-4">
                                        <Select value={member.extension} onValueChange={(v) => onMemberChange(index, 'extension', v)}>
                                            <SelectTrigger className="text-sm w-full">
                                                <SelectValue placeholder={t('pickupGroups.fields.members.extensionPlaceholder')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableForSelect.map((ext) => (
                                                    <SelectItem key={ext.id} value={ext.id}>{ext.name}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors[`member-${index}`] && (
                                            <p className="text-xs text-red-500 mt-1">{errors[`member-${index}`]}</p>
                                        )}
                                    </td>
                                    <td className="py-3 px-4">
                                        <Toggle value={member.isMember} onChange={(v) => onMemberChange(index, 'isMember', v)} />
                                    </td>
                                    <td className="py-3 px-4">
                                        <Toggle value={member.allowPickup} onChange={(v) => onMemberChange(index, 'allowPickup', v)} />
                                    </td>
                                    <td className="text-right py-3 px-4">
                                        <button
                                            type="button"
                                            onClick={() => onMemberRemove(index)}
                                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                                        >
                                            <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                <div className="flex justify-end">
                    <Button type="button" onClick={onAddMember} size="sm" className="gap-2">
                        <Plus className="h-4 w-4" />
                        {t('common.add')}
                    </Button>
                </div>
            </div>
        </FormBox>
    );
}
